import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageSquare, MapPin, Car, Calendar, Trash2, Save } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { TIME_WINDOWS } from "../../lib/data";

export const STATUS = {
  pending: { label: "Pending call", cls: "bg-amber-400/15 text-amber-300 border-amber-400/30" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
  completed: { label: "Completed", cls: "bg-sky-400/15 text-sky-300 border-sky-400/30" },
  cancelled: { label: "Cancelled", cls: "bg-red-400/15 text-red-300 border-red-400/30" },
};

const ACTIONS = [
  { s: "confirmed", label: "Confirm" },
  { s: "completed", label: "Complete" },
  { s: "cancelled", label: "Cancel" },
  { s: "pending", label: "Reset" },
];

const fmtDate = (d) => {
  const dt = new Date(`${d}T12:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

export const BookingCard = ({ b, onStatus, onNotes, onDelete }) => {
  const [notes, setNotes] = useState(b.admin_notes ?? "");
  const st = STATUS[b.status] ?? STATUS.pending;
  const tel = `tel:${(b.phone ?? "").replace(/[^\d+]/g, "")}`;
  const sms = `sms:${(b.phone ?? "").replace(/[^\d+]/g, "")}`;
  const map = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.address ?? "")}`;
  const win = TIME_WINDOWS.find((t) => t.value === b.time_window)?.label ?? b.time_window;

  return (
    <motion.article layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid={`admin-booking-card-${b.id}`} className="rounded-3xl border border-white/10 bg-[var(--surface)] p-5 sm:p-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span data-testid="admin-booking-status-badge" className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] ${st.cls}`}>{st.label}</span>
        <span className="text-xs text-zinc-400">Requested {new Date(b.created_at).toLocaleString()}</span>
      </div>
      <div className="mt-5 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <h3 className="font-race text-4xl text-white">{b.name}</h3>
          <a href={tel} className="font-mono-x mt-1 block text-lg text-[var(--pink-text)]">{b.phone}</a>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={tel} data-testid="admin-call-customer-button" className="btn-neon flex items-center gap-2 rounded-full px-5 py-3 text-sm font-bold uppercase tracking-wider text-white"><Phone className="h-4 w-4" /> Call</a>
            <a href={sms} data-testid="admin-text-customer-button" className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-bold uppercase tracking-wider text-white hover:border-white/40"><MessageSquare className="h-4 w-4" /> Text</a>
            <a href={map} target="_blank" rel="noreferrer" data-testid="admin-map-button" className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-bold uppercase tracking-wider text-white hover:border-white/40"><MapPin className="h-4 w-4" /> Map</a>
          </div>
        </div>
        <div className="space-y-3 text-sm text-zinc-300 lg:col-span-7">
          <div className="flex gap-3"><Calendar className="h-4 w-4 shrink-0 text-[var(--mag)]" /><span data-testid="admin-booking-date" className="font-semibold text-white">{fmtDate(b.preferred_date)} · {win}</span></div>
          <div className="flex gap-3"><Car className="h-4 w-4 shrink-0 text-[var(--mag)]" />{b.vehicle_year} {b.vehicle_make} {b.vehicle_model}</div>
          <div className="flex gap-3"><MapPin className="h-4 w-4 shrink-0 text-[var(--mag)]" />{b.address}</div>
          <div className="flex flex-wrap gap-2 pt-1">
            {(b.services ?? []).map((s) => <span key={s} className="rounded-full border border-white/10 bg-black/40 px-3 py-1 text-xs text-zinc-200">{s}</span>)}
          </div>
          {b.notes && <p className="rounded-xl border border-white/10 bg-black/30 p-3 text-zinc-300"><span className="text-zinc-400">Customer: </span>{b.notes}</p>}
        </div>
      </div>
      <div className="mt-6 grid gap-4 border-t border-white/10 pt-5 lg:grid-cols-12">
        <div className="flex flex-wrap gap-2 lg:col-span-5">
          {ACTIONS.filter((a) => a.s !== b.status).map((a) => (
            <button key={a.s} data-testid={`admin-status-${a.s}-button`} onClick={() => onStatus(b.id, a.s)} className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${STATUS[a.s].cls} hover:brightness-125`}>{a.label}</button>
          ))}
          <button data-testid="admin-delete-button" onClick={() => onDelete(b.id)} className="rounded-full border border-white/10 p-2 text-zinc-400 hover:text-red-300" aria-label="Delete booking"><Trash2 className="h-4 w-4" /></button>
        </div>
        <div className="flex gap-2 lg:col-span-7">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={1} placeholder="Private notes (quote, parts, arrival time…)" className="min-h-[44px] rounded-xl border-white/10 bg-black/40 text-sm text-white placeholder:text-zinc-500" data-testid="admin-notes-input" />
          <button data-testid="admin-notes-save-button" onClick={() => onNotes(b.id, notes)} className="flex shrink-0 items-center gap-2 rounded-xl border border-white/15 px-4 text-xs font-bold uppercase text-white hover:border-[var(--mag)]"><Save className="h-4 w-4" /> Save</button>
        </div>
      </div>
    </motion.article>
  );
};
